// Copyright (c) 2025 Cloudflare, Inc.
// Licensed under the MIT license found in the LICENSE.txt file or at:
//     https://opensource.org/license/mit

import { StubHook, RpcPayload, typeForRpc, RpcStub, RpcPromise, LocatedPromise, RpcTarget, unwrapStubAndPath, streamImpl, PromiseStubHook, PayloadStubHook, type RpcCallHandler } from "./core.js";
import { webSocketToStreams, makeUpgradeResponse } from "./websocket-streams.js";

export type ImportId = number;
export type ExportId = number;

/**
 * Encoding levels determine what representation the RPC system hands to the transport.
 * Each level names what the transport can assume about message values.
 *
 * - `"string"`: JSON string. Default, used by HTTP batch and WebSocket transports.
 * - `"jsonCompatible"`: JSON-compatible JS value tree. For custom encoders.
 * - `"jsonCompatibleWithBytes"`: Like `"jsonCompatible"` but Uint8Array stays raw.
 * - `"structuredClonable"`: Structured-clonable native values pass through where possible.
 *
 * @example
 * ```ts
 * // What happens to Uint8Array([1, 2, 3]) at each level:
 * "string"          → '["bytes","AQID"]'           // JSON string with base64
 * "jsonCompatible"          → ["bytes", "AQID"]      // JS array with base64
 * "jsonCompatibleWithBytes" → ["bytes", Uint8Array]  // JS array with raw bytes
 * "structuredClonable"      → ["bytes", Uint8Array]  // + Date, BigInt stay native
 * ```
 */
export type EncodingLevel = "string" | "jsonCompatible" | "jsonCompatibleWithBytes" |
    "structuredClonable";

// =======================================================================================
// Resource limits applied while deserializing messages from a peer.
//
// These guard against resource-exhaustion attacks from untrusted peers (see issue #184). They are
// purely *local, receiver-side* decisions: the protocol has no negotiation step, so a peer cannot
// learn or agree on these values. Consequently the defaults are chosen to be far larger than any
// legitimate message, so that no honest sender is ever rejected. A peer that exceeds a limit has
// its message rejected (a TypeError is thrown), which tears down the session via abort().

export interface RpcLimits {
  // Maximum number of magnitude digits permitted in a ["bigint", "..."] wire value, excluding a
  // leading "-" sign.
  //
  // Bigints are serialized as decimal strings. Decimal parsing via BigInt() is synchronous and
  // superlinear in the number of digits, so this cap bounds the worst-case parse cost.
  //
  // The default is far larger than practical big-integer or cryptographic values, but far below
  // the millions of decimal digits needed to cause meaningful blocking.
  maxBigIntDigits: number;

  // Maximum nesting depth of a single deserialized message. Guards against stack overflow from a
  // deeply-nested payload (e.g. [[[[...]]]]). This is enforced uniformly across the whole message,
  // including nested call arguments that are delivered as separate RpcPayloads.
  maxDepth: number;

  // Maximum size of a single incoming message string, measured in UTF-16 code units (JavaScript
  // String.length), before it is JSON-parsed. This is not a byte count: a string of N code units
  // may occupy more than N bytes when encoded as UTF-8. Enforced in the session read loop, after
  // the transport has already returned a complete string, to bound JSON.parse and downstream
  // deserialization work. True byte-level enforcement belongs in the transport/socket, so apps
  // exposed to untrusted peers should also configure transport/socket-native payload limits where
  // available.
  maxMessageSize: number;
}

export const DEFAULT_MAX_DEPTH = 256;

export const DEFAULT_LIMITS: RpcLimits = {
  maxBigIntDigits: 16384,
  maxDepth: DEFAULT_MAX_DEPTH,
  maxMessageSize: 32 * 1024 * 1024,
};

// =======================================================================================

const NATIVE_LITTLE_ENDIAN = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

const BYTE_CONTAINER_TYPE_NAMES = [
  "ArrayBuffer",
  "DataView",
  "Int8Array",
  "Uint8Array",
  "Uint8ClampedArray",
  "Int16Array",
  "Uint16Array",
  "Int32Array",
  "Uint32Array",
  "BigInt64Array",
  "BigUint64Array",
  "Float32Array",
  "Float64Array",
] as const;

type ByteContainerTypeName = typeof BYTE_CONTAINER_TYPE_NAMES[number];
type MarkedByteContainerTypeName = Exclude<ByteContainerTypeName, "Uint8Array">;

function isValidByteContainerName(value: string): value is ByteContainerTypeName {
  return (BYTE_CONTAINER_TYPE_NAMES as readonly string[]).includes(value);
}

// Listing every type, including those without multi-byte elements, makes adding
// a new ByteContainerTypeName a compile error until its element size is considered.
const TYPED_ARRAY_ELEMENT_SIZE: Record<ByteContainerTypeName, number | undefined> = {
  ArrayBuffer: undefined,
  DataView: undefined,
  Int8Array: undefined,
  Uint8Array: undefined,
  Uint8ClampedArray: undefined,
  Int16Array: 2,
  Uint16Array: 2,
  Int32Array: 4,
  Uint32Array: 4,
  BigInt64Array: 8,
  BigUint64Array: 8,
  Float32Array: 4,
  Float64Array: 8,
};

// Uint8Array intentionally isn't included because it uses the markerless legacy form.
const BYTE_CONTAINER_PROTOTYPES: Record<MarkedByteContainerTypeName, object> = {
  ArrayBuffer: ArrayBuffer.prototype,
  DataView: DataView.prototype,
  Int8Array: Int8Array.prototype,
  Uint8ClampedArray: Uint8ClampedArray.prototype,
  Int16Array: Int16Array.prototype,
  Uint16Array: Uint16Array.prototype,
  Int32Array: Int32Array.prototype,
  Uint32Array: Uint32Array.prototype,
  BigInt64Array: BigInt64Array.prototype,
  BigUint64Array: BigUint64Array.prototype,
  Float32Array: Float32Array.prototype,
  Float64Array: Float64Array.prototype,
};

const BYTE_CONTAINER_TYPE_BY_PROTOTYPE = new Map<object, MarkedByteContainerTypeName>();
for (let type of Object.keys(BYTE_CONTAINER_PROTOTYPES) as MarkedByteContainerTypeName[]) {
  BYTE_CONTAINER_TYPE_BY_PROTOTYPE.set(BYTE_CONTAINER_PROTOTYPES[type], type);
}

// Reverse each element's bytes in place. Production callers only need this on
// big-endian hosts; it is exported solely so the behavior can be tested on
// little-endian hosts.
export function swapByteOrder(bytes: Uint8Array, elementSize: number): void {
  if (elementSize !== 2 && elementSize !== 4 && elementSize !== 8) {
    throw new RangeError(`Unsupported element size: ${elementSize}`);
  }

  let view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let offset = 0; offset < bytes.byteLength; offset += elementSize) {
    switch (elementSize) {
      case 2:
        view.setUint16(offset, view.getUint16(offset, false), true);
        break;
      case 4:
        view.setUint32(offset, view.getUint32(offset, false), true);
        break;
      case 8:
        view.setBigUint64(offset, view.getBigUint64(offset, false), true);
        break;
    }
  }
}

export interface Exporter {
  exportStub(hook: StubHook): ExportId;
  exportPromise(hook: StubHook): ExportId;
  getImport(hook: StubHook): ImportId | undefined;

  // If a serialization error occurs after having exported some capabilities, this will be called
  // to roll back the exports.
  unexport(ids: Array<ExportId>): void;

  // Creates a pipe by sending a ["pipe"] message, then starts pumping the given ReadableStream
  // into the pipe's writable end. Returns the import ID assigned to the pipe. `hook` should be
  // disposed when the pipe finishes.
  createPipe(readable: ReadableStream, hook: StubHook): ImportId;

  onSendError(error: Error): Error | void;
}

class NullExporter implements Exporter {
  exportStub(stub: StubHook): never {
    throw new Error("Cannot serialize RPC stubs without an RPC session.");
  }
  exportPromise(stub: StubHook): never {
    throw new Error("Cannot serialize RPC stubs without an RPC session.");
  }
  getImport(hook: StubHook): ImportId | undefined {
    return undefined;
  }
  unexport(ids: Array<ExportId>): void {}
  createPipe(readable: ReadableStream): never {
    throw new Error("Cannot create pipes without an RPC session.");
  }

  onSendError(error: Error): Error | void {}
}

const NULL_EXPORTER = new NullExporter();

// Collect all bytes from a ReadableStream into a Blob with the given MIME type. Used on the
// receive side to assemble a Blob from a pipe stream before delivering to user code.
//
// `Response` is a standard global in every runtime we support (Node >=18, browsers, workerd), so
// we can rely on `Response.blob()` for the heavy lifting. `Response.blob()` may discard the
// caller-specified MIME type, so we `slice()` to reattach it if needed.
async function streamToBlob(stream: ReadableStream, type: string): Promise<Blob> {
  let b = await new Response(stream).blob();
  return b.type === type ? b : b.slice(0, b.size, type);
}

// Maps error name to error class for deserialization. Null-prototype so a wire-supplied (untrusted)
// name can't resolve to an inherited member like `constructor` (which would build a `String`
// wrapper, not an `Error`) or `toString`; unknown names fall back to `Error`.
const ERROR_TYPES: Record<string, any> = {
  __proto__: null,
  Error, EvalError, RangeError, ReferenceError, SyntaxError, TypeError, URIError, AggregateError,
  // TODO: DOMError? Others?
};

// Converts fully-hydrated messages into object trees that are JSON-serializable for sending over
// the wire. This is used to implement serialization -- but it doesn't take the last step of
// actually converting to a string. (The name is meant to be the opposite of "Evaluator", which
// implements the opposite direction.)
export class Devaluator {
  private constructor(
    private exporter: Exporter,
    private source: RpcPayload | undefined,
    private encodingLevel: EncodingLevel
  ) {}

  // Devaluate the given value.
  // * value: The value to devaluate.
  // * parent: The value's parent object, which would be used as `this` if the value were called
  //     as a function.
  // * exporter: Callbacks to the RPC session for exporting capabilities found in this message.
  // * source: The RpcPayload which contains the value, and therefore owns stubs within.
  // * encodingLevel: How much encoding to apply (default "string").
  //
  // Returns: The devaluated value, ready to be JSON-serialized (or passed to transport directly
  // for non-string levels).
  public static devaluate(
      value: unknown, parent?: object, exporter: Exporter = NULL_EXPORTER, source?: RpcPayload,
      encodingLevel: EncodingLevel = "string")
      : unknown {
    let devaluator = new Devaluator(exporter, source, encodingLevel);
    try {
      return devaluator.devaluateImpl(value, parent, 0);
    } catch (err) {
      if (devaluator.exports) {
        try {
          exporter.unexport(devaluator.exports);
        } catch (err) {
          // probably a side effect of the original error, ignore it
        }
      }
      // TODO: This rollback only releases exports. Pipes created via `createPipe` (for
      // ReadableStreams, Blobs, and the Firefox request-body fallback) have already sent a
      // ["pipe"] frame and started pumping.
      throw err;
    }
  }

  private exports?: Array<ExportId>;

  private devaluateImpl(value: unknown, parent: object | undefined, depth: number): unknown {
    if (depth >= DEFAULT_MAX_DEPTH) {
      throw new Error(
          "Serialization exceeded maximum allowed depth. (Does the message contain cycles?)");
    }

    let kind = typeForRpc(value);
    switch (kind) {
      case "unsupported": {
        let msg;
        try {
          msg = `Cannot serialize value: ${value}`;
        } catch (err) {
          msg = "Cannot serialize value: (couldn't stringify value)";
        }
        throw new TypeError(msg);
      }

      case "primitive":
        if (typeof value === "number" && !isFinite(value)) {
          // At structuredClonable level, keep Infinity/NaN as native values
          if (this.encodingLevel === "structuredClonable") {
            return value;
          }
          if (value === Infinity) {
            return ["inf"];
          } else if (value === -Infinity) {
            return ["-inf"];
          } else {
            return ["nan"];
          }
        } else {
          // Supported directly by JSON.
          return value;
        }

      case "object": {
        let object = <Record<string, unknown>>value;
        let result: Record<string, unknown> = {};
        for (let key in object) {
          result[key] = this.devaluateImpl(object[key], object, depth + 1);
        }
        return result;
      }

      case "array": {
        let array = <Array<unknown>>value;
        let len = array.length;
        let result = new Array(len);
        for (let i = 0; i < len; i++) {
          result[i] = this.devaluateImpl(array[i], array, depth + 1);
        }
        // Wrap literal arrays in an outer one-element array, to "escape" them.
        return [result];
      }

      case "bigint":
        // At structuredClonable level, keep BigInt as native value
        if (this.encodingLevel === "structuredClonable") {
          return value;
        }
        return ["bigint", (<bigint>value).toString()];

      case "date": {
        // At structuredClonable level, keep Date as native value
        if (this.encodingLevel === "structuredClonable") {
          return value;
        }
        const time = (<Date>value).getTime();
        return ["date", Number.isNaN(time) ? null : time];
      }

      case "bytes": {
        let alternateTypeName = BYTE_CONTAINER_TYPE_BY_PROTOTYPE.get(Object.getPrototypeOf(value));
        let bytes: Uint8Array;
        if (alternateTypeName === "ArrayBuffer") {
          bytes = new Uint8Array(value as ArrayBuffer);
        } else if (alternateTypeName === undefined) {
          bytes = value as Uint8Array;
        } else {
          let view = value as ArrayBufferView;
          bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
          let elementSize = TYPED_ARRAY_ELEMENT_SIZE[alternateTypeName];
          if (!NATIVE_LITTLE_ENDIAN && elementSize) {
            bytes = bytes.slice();
            swapByteOrder(bytes, elementSize);
          }
        }

        // At structuredClonable or jsonCompatibleWithBytes level, keep the bytes raw.
        if (this.encodingLevel === "structuredClonable" ||
            this.encodingLevel === "jsonCompatibleWithBytes") {
          return alternateTypeName === undefined
              ? ["bytes", bytes] : ["bytes", bytes, alternateTypeName];
        }

        let b64: string;
        if (bytes.toBase64) {
          b64 = bytes.toBase64({omitPadding: true});
        } else if (typeof Buffer !== "undefined") {
          let buf = bytes instanceof Buffer ? bytes
              : Buffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength);
          b64 = buf.toString("base64");
        } else {
          let binary = "";
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          b64 = btoa(binary);
        }
        b64 = b64.replace(/=+$/, "");
        return alternateTypeName === undefined ? ["bytes", b64] : ["bytes", b64, alternateTypeName];
      }

      case "url":
        // Always tuple-encode URLs; structured-clone support for URL isn't universal.
        return ["url", (value as URL).href];

      case "headers":
        // The `Headers` TS type apparently doesn't declare itself as being
        // Iterable<[string, string]>, but it is.
        return ["headers", [...<Iterable<[string, string]>>value]];

      case "request": {
        let req = <Request>value;
        let init: Record<string, unknown> = {};

        // For many properties below, the official Fetch spec says they must always be present,
        // but some platforms don't support them. So, we check both whether the property exists,
        // and whether it is equal to the default, before bothering to add it to `init`.

        if (req.method !== "GET") init.method = req.method;

        let headers = [...<Iterable<[string, string]>><any>req.headers];
        if (headers.length > 0) {
          // Note that we don't need to serialize this as ["headers", headers] because we are only
          // trying to create a valid RequestInit object.
          init.headers = headers;
        }

        if (req.body) {
          init.body = this.devaluateImpl(req.body, req, depth + 1);

          // Apparently the fetch spec technically requires that `duplex` be specified when a
          // body is specified, and Chrome in fact requires this, and requires the value is "half".
          // Workers hasn't implemented this (and actually supports full duplex by default, lol).
          // The TS types for Request currently don't define this property, but it is there (on
          // Chrome at least).
          init.duplex = (<any>req).duplex || "half";
        } else if (req.body === undefined &&
            !["GET", "HEAD", "OPTIONS", "TRACE", "DELETE"].includes(req.method)) {
          // If the body is undefined rather than null, most likely we're on a platform that
          // doesn't support request body streams (*cough*Firefox*cough*). We'll need to hack
          // around this by using `req.arrayBuffer()` to get the body. Unfortunately this is async,
          // so we can't just embed the resulting body into the message we are constructing. We
          // will actually have to construct a ReadableStream. Ugh!

          let bodyPromise = req.arrayBuffer();

          let readable = new ReadableStream<Uint8Array>({
            async start(controller) {
              try {
                // `as Uint8Array` is needed here to work around some sort of weird bug in the TS
                // types where `new Uint8Array` somehow doesn't return a `Uint8Array`. Instead it
                // somehow returns `Uint8Array<ArrayBuffer>` -- but `Uint8Array` is not a generic
                // type! WTF?
                // TODO(cleanup): This is apparently fixed in TS 6.
                controller.enqueue(new Uint8Array(await bodyPromise) as Uint8Array);
                controller.close();
              } catch (err) {
                controller.error(err);
              }
            }
          });

          // We can't recurse to devaluateImpl() to serialize the body because it'll call
          // source.getHookForReadableStream(), adding a hook on the payload which isn't actually
          // reachable by walking the payload, which will cause trouble later. So we have to
          // inline it a bit here...
          let hook = streamImpl.createReadableStreamHook(readable);
          let importId = this.exporter.createPipe(readable, hook);
          init.body = ["readable", importId];
          init.duplex = (<any>req).duplex || "half";
        }

        if (req.cache && req.cache !== "default") init.cache = req.cache;
        if (req.redirect !== "follow") init.redirect = req.redirect;
        if (req.integrity) init.integrity = req.integrity;

        // These properties are only meaningful in browsers and not supported by most WinterCG
        // (server-side) platforms.
        if (req.mode && req.mode !== "cors") init.mode = req.mode;
        if (req.credentials && req.credentials !== "same-origin") {
          init.credentials = req.credentials;
        }
        if (req.referrer && req.referrer !== "about:client") init.referrer = req.referrer;
        if (req.referrerPolicy) init.referrerPolicy = req.referrerPolicy;
        if (req.keepalive) init.keepalive = req.keepalive;

        // These properties are specific to Cloudflare Workers. Cast the request to `any` to
        // silence type errors on other platforms.
        let cfReq = req as any;
        if (cfReq.cf) init.cf = cfReq.cf;
        if (cfReq.encodeResponseBody && cfReq.encodeResponseBody !== "automatic") {
          init.encodeResponseBody = cfReq.encodeResponseBody;
        }

        // TODO: Support request.signal. Annoyingly, all `Request`s have a `signal` property even
        //   if none was passed to the constructor, and there's no way to tell if it's a real
        //   signal. So for now, since we don't support AbortSignal yet, all we can do is ignore
        //   it; we can't throw an error if it's present.

        return ["request", req.url, init];
      }

      case "response": {
        let resp = <Response>value;
        let cfResp = resp as any;

        // `webSocket` is a Cloudflare Workers extension indicating that the response completed
        // an HTTP/WebSocket upgrade. The socket can't be serialized as a value, so it is
        // represented as a pair of streams; see websocket-streams.ts. (Bare WebSockets, outside
        // of an upgrade Response, are intentionally not serializable.)
        let webSocket = cfResp.webSocket;
        if (webSocket && resp.body) {
          throw new TypeError("A WebSocket upgrade Response can't have a body.");
        }

        let body = this.devaluateImpl(resp.body, resp, depth + 1);
        let init: Record<string, unknown> = {};

        if (!webSocket) {
          // An upgrade implies status 101, so we don't serialize status at all in that case.
          // (We couldn't faithfully send 101 anyway: standard `Response` constructors refuse to
          // produce 1xx statuses, so the receiver may have to substitute a default.)
          if (resp.status !== 200) init.status = resp.status;
          if (resp.statusText) init.statusText = resp.statusText;
        }

        let headers = [...<Iterable<[string, string]>><any>resp.headers];
        if (headers.length > 0) {
          // Note that we don't need to serialize this as ["headers", headers] because we are only
          // trying to create a valid ResponseInit object.
          init.headers = headers;
        }

        // These properties are specific to Cloudflare Workers. We already cast the response to
        // `any` to silence type errors on other platforms.
        if (cfResp.cf) init.cf = cfResp.cf;
        if (cfResp.encodeBody && cfResp.encodeBody !== "automatic") {
          init.encodeBody = cfResp.encodeBody;
        }

        if (webSocket) {
          if (!this.source) {
            throw new Error("Can't serialize a WebSocket upgrade in this context.");
          }

          // The readable half is streamed through a pipe, exactly like a ReadableStream value:
          // messages begin flowing to the receiver immediately, before it even knows they're
          // coming, with the streams' usual flow control.
          let readableId: ImportId;
          let hook = this.source.getHookForWebSocket(webSocket, () => {
            let streams = webSocketToStreams(webSocket);
            let readableHook = streamImpl.createReadableStreamHook(streams.readable);
            readableId = this.exporter.createPipe(streams.readable, readableHook);
            return streamImpl.createWritableStreamHook(streams.writable);
          });
          init.webSocket = {
            readable: ["readable", readableId!],
            writable: this.devaluateHook("writable", hook),
          };
        }

        return ["response", body, init];
      }

      case "blob": {
        // Blobs are streamed through a pipe. This allows very large blobs to be sent without
        // causing excessively large individual messages nor blocking other messages in the
        // meantime.
        //
        // Ideally, small Blobs would be inlined. But, there is no way to read a blob
        // synchronously, and we MUST serialize the message synchronously. Hence, we have no choice
        // but to use streaming even for small blobs.
        let blob = value as Blob;
        let readable = blob.stream();
        let hook = streamImpl.createReadableStreamHook(readable);
        let importId = this.exporter.createPipe(readable, hook);
        return ["blob", blob.type, ["readable", importId]];
      }

      case "error": {
        let e = <Error>value;

        // TODO:
        // - Determine type by checking prototype rather than `name`, which can be overridden?

        let rewritten = this.exporter.onSendError(e);
        if (rewritten) {
          e = rewritten;
        }

        // Capture own enumerable properties plus the standard non-enumerable slots `cause`
        // and (for AggregateError) `errors`. Each value is run through devaluateImpl so any
        // supported type round-trips. If a property's value can't be serialized, drop the
        // property: the error itself must always make it through. Use `onSendError` to scrub
        // heavy or sensitive fields explicitly.
        //
        // On per-property failure we roll back any exports the partial walk produced by
        // splicing them off `this.exports` and unexporting them.
        //
        // TODO: this can't roll back pipes created by `createPipe` (ReadableStream, Blob,
        // Firefox request-body); the `["pipe"]` frame and pump have already started, with
        // no inverse on the `Exporter` interface, so they leak until session shutdown.
        // Same caveat as the rollback in the static `devaluate` method above.
        let anyE = <any>e;
        let props: Record<string, unknown> | undefined;
        let captureProp = (key: string, val: unknown) => {
          let exportsBefore = this.exports?.length ?? 0;
          try {
            let encoded = this.devaluateImpl(val, e, depth + 1);
            if (!props) props = {};
            props[key] = encoded;
          } catch (err) {
            // Drop this property; the error itself still propagates. Roll back any exports
            // the partial walk produced.
            if (this.exports && this.exports.length > exportsBefore) {
              let tail = this.exports.splice(exportsBefore);
              try {
                this.exporter.unexport(tail);
              } catch (err2) {
                // probably a side effect of the original error, ignore it
              }
            }
          }
        };
        for (let key of Object.keys(e)) {
          if (key === "name" || key === "message" || key === "stack") continue;
          captureProp(key, anyE[key]);
        }
        // `cause` is normally non-enumerable, so Object.keys() misses it.
        if ("cause" in e) {
          captureProp("cause", anyE.cause);
        }
        if (e instanceof AggregateError) {
          captureProp("errors", e.errors);
        }

        // Backwards-compat: only emit the new tail elements when there's something to add.
        // Errors with no extras serialize to the legacy 3- or 4-element form, byte-identical
        // to what previous versions produced.
        let result: unknown[] = ["error", e.name, e.message];
        if (props) {
          // Normalize the stack slot to null so `props` is always at index 4.
          result.push(rewritten && rewritten.stack ? rewritten.stack : null);
          result.push(props);
        } else if (rewritten && rewritten.stack) {
          result.push(rewritten.stack);
        }
        return result;
      }

      case "undefined":
        // At structuredClonable level, keep undefined as native value
        if (this.encodingLevel === "structuredClonable") {
          return undefined;
        }
        return ["undefined"];

      case "stub":
      case "rpc-promise": {
        if (!this.source) {
          throw new Error("Can't serialize RPC stubs in this context.");
        }

        let {hook, pathIfPromise} = unwrapStubAndPath(<RpcStub>value);
        let importId = this.exporter.getImport(hook);
        if (importId !== undefined) {
          if (pathIfPromise) {
            // It's a promise pointing back to the peer, so we are doing pipelining here.
            if (pathIfPromise.length > 0) {
              return ["pipeline", importId, pathIfPromise];
            } else {
              return ["pipeline", importId];
            }
          } else {
            return ["import", importId];
          }
        }

        if (pathIfPromise) {
          hook = hook.get(pathIfPromise);
        } else {
          hook = hook.dup();
        }

        return this.devaluateHook(pathIfPromise ? "promise" : "export", hook);
      }

      case "function":
      case "rpc-target": {
        if (!this.source) {
          throw new Error("Can't serialize RPC stubs in this context.");
        }

        let hook = this.source.getHookForRpcTarget(<RpcTarget|Function>value, parent);
        return this.devaluateHook("export", hook);
      }

      case "rpc-thenable": {
        if (!this.source) {
          throw new Error("Can't serialize RPC stubs in this context.");
        }

        let hook = this.source.getHookForRpcTarget(<RpcTarget>value, parent);
        return this.devaluateHook("promise", hook);
      }

      case "writable": {
        if (!this.source) {
          throw new Error("Can't serialize WritableStream in this context.");
        }

        let hook = this.source.getHookForWritableStream(<WritableStream>value, parent);
        return this.devaluateHook("writable", hook);
      }

      case "readable": {
        if (!this.source) {
          throw new Error("Can't serialize ReadableStream in this context.");
        }

        let ws = <ReadableStream>value;
        let hook = this.source.getHookForReadableStream(ws, parent);

        // Create a pipe and start pumping the ReadableStream into it.
        let importId = this.exporter.createPipe(ws, hook);

        return ["readable", importId];
      }

      default:
        kind satisfies never;
        throw new Error("unreachable");
    }
  }

  private devaluateHook(type: "export" | "promise" | "writable", hook: StubHook): unknown {
    if (!this.exports) this.exports = [];
    let exportId = type === "promise" ? this.exporter.exportPromise(hook)
                                      : this.exporter.exportStub(hook);
    this.exports.push(exportId);
    return [type, exportId];
  }
}

/**
 * Serialize a value, using Cap'n Web's underlying serialization. This won't be able to serialize
 * RPC stubs, but it will support basic data types.
 */
export function serialize(value: unknown): string {
  return JSON.stringify(Devaluator.devaluate(value));
}

// =======================================================================================

export interface Importer {
  importStub(idx: ImportId): StubHook;
  importPromise(idx: ImportId): StubHook;
  getExport(idx: ExportId): StubHook | undefined;

  // Retrieves the ReadableStream end of a pipe created by a ["pipe"] message.
  // The exportId must refer to an export that was created as a pipe.
  // This can only be called once per pipe.
  getPipeReadable(exportId: ExportId): ReadableStream;

  // The resource limits the Evaluator should enforce while deserializing. Surfaced through the
  // Importer (rather than the Evaluator constructor) so that the per-session options reach the
  // Evaluator without changing how Evaluators are constructed throughout the codebase.
  getLimits(): RpcLimits;
}

class NullImporter implements Importer {
  importStub(idx: ImportId): never {
    throw new Error("Cannot deserialize RPC stubs without an RPC session.");
  }
  importPromise(idx: ImportId): never {
    throw new Error("Cannot deserialize RPC stubs without an RPC session.");
  }
  getExport(idx: ExportId): StubHook | undefined {
    return undefined;
  }
  getPipeReadable(exportId: ExportId): never {
    throw new Error("Cannot retrieve pipe readable without an RPC session.");
  }
  getLimits(): RpcLimits {
    // The standalone deserialize() entry point has no session and therefore no per-session
    // overrides, but it must still be protected -- so it enforces the bare defaults.
    return DEFAULT_LIMITS;
  }
}

const NULL_IMPORTER = new NullImporter();

// Some runtimes (Firefox) don't support `request.body` as a stream, but we receive request bodies
// as streams. We'll need to read the body into an ArrayBuffer and recreate the request. This is
// asynchronous, so we'll have to swap in a promise here. This potentially breaks e-order but
// that's something people will just have to live with when sending a Request to a Firefox
// endpoint (probably rare).
function fixBrokenRequestBody(request: Request, body: ReadableStream): RpcPromise {
  // Reuse built-in code to read the stream into an array.
  let promise = new Response(body).arrayBuffer().then(arrayBuffer => {
    let bytes = new Uint8Array(arrayBuffer);
    let result = new Request(request, {body: bytes});
    return new PayloadStubHook(RpcPayload.fromAppReturn(result));
  });
  return new RpcPromise(new PromiseStubHook(promise), []);
}

// Unfortuntaely, even though Blobs can only be read asynchronously, there is no way to create
// a blob backed by an asynchronous source; the bytes MUST all be provided upfront. This
// effectively makes it impossible to manitain e-order when sending Blobs.
//
// As a compromise, we deliver a message as if it contained an RpcPromise that resolves to the
// Blob. This has the effect that the RPC system will wait for the whole Blob to stream in before
// delivering the message -- reusing the existing machinery for handling promises.
function streamToBlobPromise(stream: ReadableStream, type: string): RpcPromise {
  let promise = streamToBlob(stream, type).then(blob => {
    return new PayloadStubHook(RpcPayload.fromAppReturn(blob));
  });
  return new RpcPromise(new PromiseStubHook(promise), []);
}

// Takes object trees parse from JSON and converts them into fully-hydrated JavaScript objects for
// delivery to the app. This is used to implement deserialization, except that it doesn't actually
// start from a raw string.
export class Evaluator {
  private limits: RpcLimits;

  constructor(private importer: Importer, private encodingLevel: EncodingLevel = "string",
              private callHandler?: RpcCallHandler) {
    this.limits = importer.getLimits();
  }

  private hooks: StubHook[] = [];
  private promises: LocatedPromise[] = [];

  public evaluate(value: unknown): RpcPayload {
    return this.evaluateWithDepth(value, 0);
  }

  private evaluateWithDepth(value: unknown, depth: number): RpcPayload {
    let payload = RpcPayload.forEvaluate(this.hooks, this.promises, this.callHandler);
    try {
      payload.value = this.evaluateImpl(value, payload, "value", depth);
      return payload;
    } catch (err) {
      payload.dispose();
      throw err;
    }
  }

  // Evaluate the value without destroying it.
  public evaluateCopy(value: unknown): RpcPayload {
    return this.evaluate(structuredClone(value));
  }

  private evaluateImpl(
      value: unknown, parent: object, property: string | number, depth: number): unknown {
    let maxDepth = this.limits.maxDepth;
    if (depth >= maxDepth) {
      throw new TypeError(
          `Deserialization exceeded maximum allowed message depth of ${maxDepth}.`);
    }

    // At structuredClonable level, some native types pass through devaluation unencoded: Date and
    // BigInt (as well as undefined and non-finite numbers, which the generic paths below already
    // handle). Note that bytes and errors are tuple-encoded at every level, so raw `Uint8Array`
    // and `Error` values are intentionally *not* accepted here.
    if (this.encodingLevel === "structuredClonable") {
      if (value instanceof Date || typeof value === "bigint") {
        return value;
      }
    }

    if (value instanceof Array) {
      if (value.length == 1 && value[0] instanceof Array) {
        // Escaped array. Evaluate the contents.
        let result = value[0];
        for (let i = 0; i < result.length; i++) {
          result[i] = this.evaluateImpl(result[i], result, i, depth + 1);
        }
        return result;
      } else switch (value[0]) {
        case "bigint":
          if (typeof value[1] == "string") {
            let digits = value[1];
            let maxBigIntDigits = this.limits.maxBigIntDigits;
            // Cap the length before the superlinear BigInt() parse (DoS guard).
            if (digits.length > maxBigIntDigits) {
              throw new TypeError(
                  `Deserialized bigint exceeds maximum length of ${maxBigIntDigits} digits.`);
            }
            return BigInt(digits);
          }
          break;
        case "date":
          if (value[1] === null) {
            return new Date(NaN);
          }
          if (typeof value[1] == "number") {
            return new Date(value[1]);
          }
          break;
        case "bytes": {
          let bytes: Uint8Array;
          // At jsonCompatibleWithBytes/structuredClonable level, bytes may already be raw.
          if (value[1] instanceof Uint8Array) {
            bytes = value[1];
          } else if (typeof value[1] == "string") {
            if (typeof Buffer !== "undefined") {
              bytes = Buffer.from(value[1], "base64");
            } else if (Uint8Array.fromBase64) {
              bytes = Uint8Array.fromBase64(value[1]);
            } else {
              let bs = atob(value[1]);
              let len = bs.length;
              bytes = new Uint8Array(len);
              for (let i = 0; i < len; i++) {
                bytes[i] = bs.charCodeAt(i);
              }
            }
          } else {
            break;
          }

          if (value.length === 2) {
            return bytes;
          }
          if (typeof value[2] !== "string") {
            throw new TypeError(`Unknown bytes type marker type: ${typeof value[2]}`);
          }

          if (!isValidByteContainerName(value[2])) {
            let marker = value[2].slice(0, 64);
            throw new TypeError(`Unknown bytes type marker: ${marker}`);
          }

          let marker = value[2];
          let elementSize = TYPED_ARRAY_ELEMENT_SIZE[marker];
          if (elementSize !== undefined && bytes.byteLength % elementSize !== 0) {
            throw new TypeError(
                `Invalid byte length ${bytes.byteLength} for ${marker}; ` +
                `expected a multiple of ${elementSize}`);
          }

          // Copy exactly the decoded range rather than exposing or aliasing a pooled Buffer.
          let buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
          if (!NATIVE_LITTLE_ENDIAN && elementSize !== undefined) {
            swapByteOrder(new Uint8Array(buffer), elementSize);
          }
          switch (marker) {
            case "ArrayBuffer": return buffer;
            case "DataView": return new DataView(buffer);
            case "Int8Array": return new Int8Array(buffer);
            case "Uint8Array": return new Uint8Array(buffer);
            case "Uint8ClampedArray": return new Uint8ClampedArray(buffer);
            case "Int16Array": return new Int16Array(buffer);
            case "Uint16Array": return new Uint16Array(buffer);
            case "Int32Array": return new Int32Array(buffer);
            case "Uint32Array": return new Uint32Array(buffer);
            case "BigInt64Array": return new BigInt64Array(buffer);
            case "BigUint64Array": return new BigUint64Array(buffer);
            case "Float32Array": return new Float32Array(buffer);
            case "Float64Array": return new Float64Array(buffer);
            default: marker satisfies never;
          }
        }
        case "error":
          if (value.length >= 3 && typeof value[1] === "string" && typeof value[2] === "string") {
            let cls = ERROR_TYPES[value[1]] || Error;
            // AggregateError's constructor takes (errors, message); we pass an empty array
            // and patch `errors` from the props bag below.
            let result = cls === AggregateError ? new cls([], value[2]) : new cls(value[2]);
            if (typeof value[3] === "string") {
              result.stack = value[3];
            }
            // Optional 5th element: own properties bag. Unknown keys are assigned as own
            // enumerable properties so the receiver sees what the sender attached.
            if (value.length >= 5) {
              let props = value[4];
              if (!props || typeof props !== "object" || Array.isArray(props)) {
                break;  // malformed; fall through to the "unknown special value" throw
              }
              let anyResult = <any>result;
              let propsObj = <Record<string, unknown>>props;
              for (let key of Object.keys(propsObj)) {
                if (key === "name" || key === "message" || key === "stack") continue;
                if (key in Object.prototype || key === "toJSON") {
                  // Consistent with the plain-object deserializer below: don't allow error
                  // properties to override Object.prototype members (e.g. __proto__, toString,
                  // valueOf) or toJSON. Still evaluate the inner value so any stubs are released.
                  this.evaluateImpl(propsObj[key], result, key, depth + 1);
                  continue;
                }
                anyResult[key] = this.evaluateImpl(propsObj[key], result, key, depth + 1);
              }
            }
            return result;
          }
          break;
        case "undefined":
          if (value.length === 1) {
            return undefined;
          }
          break;
        case "inf":
          return Infinity;
        case "-inf":
          return -Infinity;
        case "nan":
          return NaN;

        case "url":
          if (value.length === 2 && typeof value[1] === "string") {
            return new URL(value[1]);
          }
          break;

        case "headers":
          // We only need to validate that the parameter is an array, so as not to invoke an
          // unexpected variant of the Headers constructor. So long as it is an array then we can
          // rely on the constructor to perform type checking.
          if (value.length === 2 && value[1] instanceof Array) {
            return new Headers(value[1] as [string, string][]);
          }
          break;

        case "request": {
          if (value.length !== 3 || typeof value[1] !== "string") break;
          let url = value[1] as string;
          let init = value[2];
          if (typeof init !== "object" || init === null) break;

          // Evaluate specific properties which are expected to contain non-trivial types.
          if (init.body) {
            init.body = this.evaluateImpl(init.body, init, "body", depth + 1);
            if (init.body === null ||
                typeof init.body === "string" ||
                init.body instanceof Uint8Array ||
                init.body instanceof ReadableStream) {
              // Acceptable types.
            } else {
              throw new TypeError("Request body must be of type ReadableStream.");
            }
          }
          if (init.signal) {
            init.signal = this.evaluateImpl(init.signal, init, "signal", depth + 1);
            if (!(init.signal instanceof AbortSignal)) {
              throw new TypeError("Request siganl must be of type AbortSignal.");
            }
          }

          // Type-check `headers` is an array because the constructor allows multiple
          // representations and we don't want to allow the others.
          if (init.headers && !(init.headers instanceof Array)) {
            throw new TypeError("Request headers must be serialized as an array of pairs.");
          }

          // We assume the `Request` constructor can type-check the remaining properties.
          let result = new Request(url, init as RequestInit);

          if (init.body instanceof ReadableStream && result.body === undefined) {
            // Oh no! We must be on Firefox where request bodies are not supported, but we had a
            // body.
            let promise = fixBrokenRequestBody(result, init.body);
            this.promises.push({promise, parent, property});
            return promise;
          } else {
            return result;
          }
        }

        case "response": {
          if (value.length !== 3) break;

          let body = this.evaluateImpl(value[1], parent, property, depth + 1);
          if (body === null ||
              typeof body === "string" ||
              body instanceof Uint8Array ||
              body instanceof ReadableStream) {
            // Acceptable types.
          } else {
            throw new TypeError("Response body must be of type ReadableStream.");
          }

          let init = value[2];
          if (typeof init !== "object" || init === null) break;

          // Type-check `headers` is an array because the constructor allows multiple
          // representations and we don't want to allow the others.
          if (init.headers && !(init.headers instanceof Array)) {
            throw new TypeError("Request headers must be serialized as an array of pairs.");
          }

          // Evaluate specific properties which are expected to contain non-trivial types.
          if (init.webSocket) {
            // `response.webSocket` is a Cloudflare Workers extension, indicating the response
            // completed an HTTP/WebSocket upgrade. It is serialized as a pair of streams; see
            // websocket-streams.ts.
            if (body !== null) {
              throw new TypeError("A WebSocket upgrade Response can't have a body.");
            }
            let ws = init.webSocket;
            if (typeof ws !== "object" || ws === null || ws instanceof Array) {
              throw new TypeError("Response webSocket must be serialized as a pair of streams.");
            }

            let readable = this.evaluateImpl(ws.readable, ws, "readable", depth + 1);
            if (!(readable instanceof ReadableStream)) {
              throw new TypeError("Response webSocket readable must be a ReadableStream.");
            }

            // We import the writable's hook directly rather than wrapping it in a proxy
            // WritableStream, because the receiving socket needs to manage the hook's lifetime
            // itself (it takes its own reference when the app claims the socket; see
            // TunneledWebSocket).
            let writable = ws.writable;
            if (!(writable instanceof Array) || writable.length !== 2 ||
                writable[0] !== "writable" || typeof writable[1] !== "number") {
              throw new TypeError("Response webSocket writable must be a WritableStream.");
            }
            let writableHook = this.importer.importStub(writable[1]);
            this.hooks.push(writableHook);

            delete init.webSocket;
            return makeUpgradeResponse(readable, writableHook, init as ResponseInit);
          }

          return new Response(body as BodyInit | null, init as ResponseInit);
        }

        case "blob": {
          // Wire format is strictly ["blob", type, ["readable", id]] — the encoder always streams
          // bytes through a pipe, so the content expression must evaluate to a ReadableStream.
          if (value.length !== 3 || typeof value[1] !== "string") break;
          let contentType = value[1] as string;
          let content = this.evaluateImpl(value[2], parent, property, depth + 1);
          if (!(content instanceof ReadableStream)) {
            throw new TypeError("Blob content must be serialized as a ReadableStream.");
          }
          // Reuse the RpcPromise infrastructure (same pattern as fixBrokenRequestBody): the
          // payload-delivery machinery resolves the promise and substitutes the real Blob before
          // user code sees the value.
          let promise = streamToBlobPromise(content, contentType);
          this.promises.push({promise, parent, property});
          return promise;
        }

        case "import":
        case "pipeline": {
          // It's an "import" from the perspective of the sender, so it's an export from our
          // side. In other words, the sender is passing our own object back to us.

          if (value.length < 2 || value.length > 4) {
            break;   // report error below
          }

          // First parameter is import ID (from the sender's perspective, so export ID from
          // ours).
          if (typeof value[1] != "number") {
            break;   // report error below
          }

          let hook = this.importer.getExport(value[1]);
          if (!hook) {
            throw new Error(`no such entry on exports table: ${value[1]}`);
          }

          let isPromise = value[0] == "pipeline";

          let addStub = (hook: StubHook) => {
            if (isPromise) {
              let promise = new RpcPromise(hook, []);
              this.promises.push({promise, parent, property});
              return promise;
            } else {
              this.hooks.push(hook);
              return new RpcPromise(hook, []);
            }
          };

          if (value.length == 2) {
            // Just referencing the export itself.
            if (isPromise) {
              // We need to use hook.get([]) to make sure we get a promise hook.
              return addStub(hook.get([]));
            } else {
              // dup() returns a stub hook.
              return addStub(hook.dup());
            }
          }

          // Second parameter, if given, is a property path.
          let path = value[2];
          if (!(path instanceof Array)) {
            break;  // report error below
          }
          if (!path.every(
              part => { return typeof part == "string" || typeof part == "number"; })) {
            break;  // report error below
          }

          if (value.length == 3) {
            // Just referencing the path, not a call.
            return addStub(hook.get(path));
          }

          // Third parameter, if given, is call arguments. The sender has identified a function
          // and wants us to call it.
          //
          // Usually this is used with "pipeline", in which case we evaluate to an
          // RpcPromise. However, this can be used with "import", in which case the caller is
          // asking that the result be coerced to RpcStub. This distinction matters if the
          // result of this evaluation is to be passed as arguments to another call -- promises
          // must be resolved in advance, but stubs can be passed immediately.
          let args = value[3];
          if (!(args instanceof Array)) {
            break;  // report error below
          }

          // We need a new evaluator for the args, to build a separate payload.
          let subEval = new Evaluator(this.importer, this.encodingLevel, this.callHandler);
          args = subEval.evaluateWithDepth([args], depth);

          return addStub(hook.call(path, args));
        }

        case "remap": {
          if (value.length !== 5 ||
              typeof value[1] !== "number" ||
              !(value[2] instanceof Array) ||
              !(value[3] instanceof Array) ||
              !(value[4] instanceof Array)) {
            break;   // report error below
          }

          let hook = this.importer.getExport(value[1]);
          if (!hook) {
            throw new Error(`no such entry on exports table: ${value[1]}`);
          }

          let path = value[2];
          if (!path.every(
              part => { return typeof part == "string" || typeof part == "number"; })) {
            break;  // report error below
          }

          let captures: StubHook[] = value[3].map(cap => {
            if (!(cap instanceof Array) ||
                cap.length !== 2 ||
                (cap[0] !== "import" && cap[0] !== "export") ||
                typeof cap[1] !== "number") {
              throw new TypeError(`unknown map capture: ${JSON.stringify(cap)}`);
            }

            if (cap[0] === "export") {
              return this.importer.importStub(cap[1]);
            } else {
              let exp = this.importer.getExport(cap[1]);
              if (!exp) {
                throw new Error(`no such entry on exports table: ${cap[1]}`);
              }
              return exp.dup();
            }
          });

          let instructions = value[4];

          let resultHook = hook.map(path, captures, instructions);

          let promise = new RpcPromise(resultHook, []);
          this.promises.push({promise, parent, property});
          return promise;
        }

        case "export":
        case "promise":
          // It's an "export" from the perspective of the sender, i.e. they sent us a new object
          // which we want to import.
          //
          // "promise" is same as "export" but should not be delivered to the application. If any
          // promises appear in a value, they must be resolved and substituted with their results
          // before delivery. Note that if the value being evaluated appeared in call params, or
          // appeared in a resolve message for a promise that is being pulled, then the new promise
          // is automatically also being pulled, otherwise it is not.
          if (typeof value[1] == "number") {
            if (value[0] == "promise") {
              let hook = this.importer.importPromise(value[1]);
              let promise = new RpcPromise(hook, []);
              this.promises.push({parent, property, promise});
              return promise;
            } else {
              let hook = this.importer.importStub(value[1]);
              this.hooks.push(hook);
              return new RpcStub(hook);
            }
          }
          break;

        case "writable":
          // It's a WritableStream export from the sender. We import it and create a proxy
          // WritableStream that forwards writes to the remote end.
          if (typeof value[1] == "number") {
            let hook = this.importer.importStub(value[1]);
            let stream = streamImpl.createWritableStreamFromHook(hook);
            // Track the stream for disposal.
            this.hooks.push(hook);
            return stream;
          }
          break;

        case "readable":
          // References the readable end of a pipe. The import ID (from the sender's perspective)
          // is our export ID.
          if (typeof value[1] == "number") {
            let stream = this.importer.getPipeReadable(value[1]);
            // Track the stream for disposal so that if the payload is disposed before the
            // app reads the stream, the ReadableStream is properly canceled.
            let hook = streamImpl.createReadableStreamHook(stream);
            this.hooks.push(hook);
            return stream;
          }
          break;
      }
      throw new TypeError(`unknown special value: ${JSON.stringify(value)}`);
    } else if (value instanceof Object) {
      let result = <Record<string, unknown>>value;
      for (let key in result) {
        if (key in Object.prototype || key === "toJSON") {
          // Out of an abundance of caution, we will ignore properties that override properties
          // of Object.prototype. It's especially important that we don't allow `__proto__` as it
          // may lead to prototype pollution. We also would rather not allow, e.g., `toString()`,
          // as overriding this could lead to various mischief.
          //
          // We also block `toJSON()` for similar reasons -- even though Object.prototype doesn't
          // actually define it, `JSON.stringify()` treats it specially and we don't want someone
          // snooping on JSON calls.
          //
          // We do still evaluate the inner value so that we can properly release any stubs.
          this.evaluateImpl(result[key], result, key, depth + 1);
          delete result[key];
        } else {
          result[key] = this.evaluateImpl(result[key], result, key, depth + 1);
        }
      }
      return result;
    } else {
      // Other JSON types just pass through.
      return value;
    }
  }
}

/**
 * Deserialize a value serialized using serialize().
 */
export function deserialize(value: string): unknown {
  let payload = new Evaluator(NULL_IMPORTER).evaluate(JSON.parse(value));
  payload.dispose();  // should be no-op but just in case
  return payload.value;
}
